import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Form, Switch, Select, Alert, Space, Typography, Divider, Radio, InputNumber, Input } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import DataSetQueryConditionGroupsEditor from './DataSetQueryConditionGroupsEditor';
import DataSetQueryOrderByEditor from './DataSetQueryOrderByEditor';

function normalizeKbDraft(data) {
  return {
    /** 總開關「使用知識庫」：與節點 knowledgeBaseEnabled 同步 */
    masterKb: !!data.knowledgeBaseEnabled,
    knowledgeBaseDataSetId: data.knowledgeBaseDataSetId || '',
    knowledgeBaseQueryConditionGroups: Array.isArray(data.knowledgeBaseQueryConditionGroups)
      ? data.knowledgeBaseQueryConditionGroups
      : [],
    knowledgeBaseQueryOrderBy: Array.isArray(data.knowledgeBaseQueryOrderBy)
      ? data.knowledgeBaseQueryOrderBy
      : [],
    knowledgeStudioDocumentIds: Array.isArray(data.knowledgeStudioDocumentIds)
      ? data.knowledgeStudioDocumentIds
      : [],
    // 日曆事件知識庫
    knowledgeBaseCalendarEnabled: !!data.knowledgeBaseCalendarEnabled,
    knowledgeBaseCalendarFilterMode: data.knowledgeBaseCalendarFilterMode || 'self',
    knowledgeBaseCalendarGroupIds: Array.isArray(data.knowledgeBaseCalendarGroupIds)
      ? data.knowledgeBaseCalendarGroupIds
      : [],
    knowledgeBaseCalendarHashtags: Array.isArray(data.knowledgeBaseCalendarHashtags)
      ? data.knowledgeBaseCalendarHashtags
      : [],
    knowledgeBaseCalendarRelativeDays:
      data.knowledgeBaseCalendarRelativeDays != null ? data.knowledgeBaseCalendarRelativeDays : 30,
    knowledgeStudioRagEnabled: !!data.knowledgeStudioRagEnabled,
    knowledgeStudioTopK: data.knowledgeStudioTopK != null ? data.knowledgeStudioTopK : 8,
    knowledgeStudioMaxContextChars:
      data.knowledgeStudioMaxContextChars != null ? data.knowledgeStudioMaxContextChars : 8000,
    knowledgeStudioEmbeddingProviderKey: data.knowledgeStudioEmbeddingProviderKey || '',
    knowledgeStudioRagProvider: data.knowledgeStudioRagProvider || 'local',
    knowledgeStudioCollectionIds: Array.isArray(data.knowledgeStudioCollectionIds)
      ? data.knowledgeStudioCollectionIds
      : [],
    ragAdditionalQuery: data.ragAdditionalQuery || '',
  };
}

function computeKnowledgeBaseEnabled(draft) {
  if (!draft.masterKb) return false;
  const hasStudio = (draft.knowledgeStudioDocumentIds || []).length > 0;
  const hasDataset = !!draft.knowledgeBaseDataSetId;
  return hasStudio || hasDataset;
}

/**
 * 流程設計器：抽屜「使用知識庫」開啟彈窗；內含總開關、知識庫文件（多選）、知識庫 Dataset 與查詢條件，
 * 以及第三類來源「日曆事件」（依人員篩選模式查詢 CalendarEvent）。
 */
export default function WorkflowKnowledgeBaseSettingsControl({
  nodeData,
  onApply,
  showDivider = false,
  dataSets = [],
  loadingDataSets = false,
  knowledgeStudioDocs = [],
  loadingKnowledgeStudioDocs = false,
  processVariables = [],
  generateWhereClause,
  t,
  labels = {},
  broadcastGroups = [],
  loadingBroadcastGroups = false,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => normalizeKbDraft(nodeData || {}));
  const [modalColumns, setModalColumns] = useState([]);
  const [embeddingProviders, setEmbeddingProviders] = useState([]);
  const [loadingEmbeddingProviders, setLoadingEmbeddingProviders] = useState(false);
  const [isXaiDefaultProvider, setIsXaiDefaultProvider] = useState(false);

  const dsLabel = labels.dataSetLabelKey ? t(labels.dataSetLabelKey) : t('workflowDesigner.waitReplyKnowledgeBaseDataSet');

  const loadModalKbColumns = useCallback(async (dataSetId) => {
    if (!dataSetId) {
      setModalColumns([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/datasets/${dataSetId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('load columns failed');
      }
      const data = await response.json();
      setModalColumns(data.data?.columns || []);
    } catch {
      setModalColumns([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeKbDraft(nodeData || {}));
  }, [open, nodeData]);

  useEffect(() => {
    if (!open) return;
    void loadModalKbColumns(draft.knowledgeBaseDataSetId || null);
  }, [open, draft.knowledgeBaseDataSetId, loadModalKbColumns]);

  useEffect(() => {
    if (!open) return;
    const loadEmbeddingProviders = async () => {
      setLoadingEmbeddingProviders(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/apiproviders/company?category=Embedding', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('load embedding providers failed');
        const data = await response.json();
        setEmbeddingProviders(Array.isArray(data) ? data : []);
      } catch {
        setEmbeddingProviders([]);
      } finally {
        setLoadingEmbeddingProviders(false);
      }
    };
    void loadEmbeddingProviders();
  }, [open]);

  // 偵測預設 AI provider 是否為 xAI（自動切換至 xai-collections 模式）
  useEffect(() => {
    if (!open) return;
    const detectXaiProvider = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/apiproviders/company?category=AI', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const aiData = await res.json();
          const activeAi = Array.isArray(aiData) ? aiData.find((p) => p.active) : null;
          const isXai = activeAi
            ? activeAi.providerKey === 'xai' ||
              String(activeAi.providerKey || '').toLowerCase().startsWith('xai')
            : false;
          setIsXaiDefaultProvider(isXai);
          if (isXai) {
            setDraft((prev) => ({ ...prev, knowledgeStudioRagProvider: 'xai-collections' }));
          }
        }
      } catch {
        // 非致命錯誤，忽略
      }
    };
    void detectXaiProvider();
  }, [open]);

  const datasetName =
    (dataSets || []).find((ds) => String(ds.id) === String(draft.knowledgeBaseDataSetId))?.name || '';
  const studioCount = (draft.knowledgeStudioDocumentIds || []).length;
  const ragProvider = draft.knowledgeStudioRagProvider || 'local';
  // RAG 可啟用條件：自建管線需有本地文件；xAI Collections 只需啟用 provider（collection 由分類自動解析）
  const ragEligible = studioCount > 0 || ragProvider === 'xai-collections';

  // 摘要文字
  let summary = t('workflowDesigner.knowledgeBaseSettingsSummaryEmpty');
  const parts = [];
  if (draft.masterKb) {
    if (draft.knowledgeBaseDataSetId) {
      let s = t('workflowDesigner.knowledgeBaseSettingsSummaryDataset', {
        name: datasetName || draft.knowledgeBaseDataSetId,
      });
      if (studioCount > 0) {
        s += t('workflowDesigner.knowledgeBaseSettingsSummaryAndStudio', { count: studioCount });
      }
      parts.push(s);
    } else if (studioCount > 0) {
      parts.push(t('workflowDesigner.knowledgeBaseSettingsSummaryStudioOnly', { count: studioCount }));
    } else {
      parts.push(t('workflowDesigner.knowledgeBaseSettingsSummaryMasterOn'));
    }
  }
  if (draft.knowledgeBaseCalendarEnabled) {
    if (draft.knowledgeBaseCalendarFilterMode === 'groupTag') {
      const n = (draft.knowledgeBaseCalendarGroupIds || []).length;
      const tagCount = (draft.knowledgeBaseCalendarHashtags || []).length;
      const tagsStr = tagCount > 0 ? `；${tagCount} 標籤` : '';
      parts.push(t('workflowDesigner.knowledgeBaseSettingsSummaryCalendarGroup', { n, tags: tagsStr }));
    } else {
      parts.push(t('workflowDesigner.knowledgeBaseSettingsSummaryCalendarSelf'));
    }
  }
  if (parts.length > 0) summary = parts.join('；');

  const disabled = !draft.masterKb;
  const calendarGroupTagMode = draft.knowledgeBaseCalendarFilterMode === 'groupTag';

  const handleOk = () => {
    const kbEnabled = computeKnowledgeBaseEnabled(draft);
    const dsId = draft.masterKb ? draft.knowledgeBaseDataSetId : '';
    onApply({
      knowledgeBaseEnabled: kbEnabled,
      knowledgeBaseDatasetEnabled: draft.masterKb && !!dsId,
      knowledgeBaseDataSetId: dsId,
      knowledgeBaseQueryConditionGroups: draft.masterKb && dsId ? draft.knowledgeBaseQueryConditionGroups : [],
      knowledgeBaseQueryOrderBy: draft.masterKb && dsId ? draft.knowledgeBaseQueryOrderBy || [] : [],
      knowledgeStudioDocumentIds: draft.masterKb ? draft.knowledgeStudioDocumentIds || [] : [],
      knowledgeStudioRagEnabled: draft.masterKb && ragEligible ? !!draft.knowledgeStudioRagEnabled : false,
      knowledgeStudioRagProvider: ragProvider,
      knowledgeStudioCollectionIds:
        ragProvider === 'xai-collections' ? (draft.knowledgeStudioCollectionIds || []) : [],
      knowledgeStudioTopK: draft.knowledgeStudioTopK,
      knowledgeStudioMaxContextChars: draft.knowledgeStudioMaxContextChars,
      knowledgeStudioEmbeddingProviderKey: draft.knowledgeStudioEmbeddingProviderKey || '',
      // 日曆事件
      knowledgeBaseCalendarEnabled: draft.knowledgeBaseCalendarEnabled,
      knowledgeBaseCalendarFilterMode: draft.knowledgeBaseCalendarEnabled
        ? draft.knowledgeBaseCalendarFilterMode
        : 'self',
      knowledgeBaseCalendarGroupIds: draft.knowledgeBaseCalendarEnabled && calendarGroupTagMode
        ? draft.knowledgeBaseCalendarGroupIds || []
        : [],
      knowledgeBaseCalendarHashtags: draft.knowledgeBaseCalendarEnabled && calendarGroupTagMode
        ? draft.knowledgeBaseCalendarHashtags || []
        : [],
      knowledgeBaseCalendarRelativeDays: draft.knowledgeBaseCalendarEnabled
        ? draft.knowledgeBaseCalendarRelativeDays
        : null,
      ragAdditionalQuery: draft.ragAdditionalQuery || '',
    });
    setOpen(false);
  };

  const updateDraft = (partial) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  return (
    <>
      {showDivider ? <Divider style={{ margin: '16px 0' }} /> : null}
      <Form.Item
        label={t('workflowDesigner.knowledgeBaseSettingsDrawerLabel')}
        tooltip={t('workflowDesigner.knowledgeBaseSettingsDrawerHelp')}
      >
        <Space align="center" wrap>
          <Button type="default" icon={<SettingOutlined />} onClick={() => setOpen(true)}>
            {t('workflowDesigner.knowledgeBaseSettingsConfigure')}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {summary}
          </Typography.Text>
        </Space>
      </Form.Item>

      <Modal
        title={t('workflowDesigner.knowledgeBaseSettingsModalTitle')}
        open={open}
        onOk={handleOk}
        onCancel={() => setOpen(false)}
        width={880}
        destroyOnClose={false}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingTop: 12 } }}
        okText={t('workflowDesigner.knowledgeBaseSettingsOk')}
        cancelText={t('workflowDesigner.knowledgeBaseSettingsCancel')}
      >
        <Alert
          type="info"
          showIcon
          message={t('workflowDesigner.knowledgeSourcesHint')}
          style={{ marginBottom: 16 }}
        />

        <Space align="center" style={{ marginBottom: 4 }}>
          <Switch
            checked={!!draft.masterKb}
            onChange={(checked) => {
              updateDraft({
                masterKb: checked,
                ...(!checked
                  ? {
                      knowledgeBaseDataSetId: '',
                      knowledgeBaseQueryConditionGroups: [],
                      knowledgeStudioDocumentIds: [],
                    }
                  : {}),
              });
            }}
          />
          <Typography.Text strong>{t('workflowDesigner.knowledgeBaseSettingsMasterSwitch')}</Typography.Text>
        </Space>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
          {t('workflowDesigner.knowledgeBaseSettingsMasterHelp')}
        </Typography.Paragraph>

        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
          {t('workflowDesigner.knowledgeBaseSettingsSectionStudio')}
        </Typography.Title>
        <Form.Item tooltip={t('workflowDesigner.knowledgeSourceStudioHelp')}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            disabled={disabled}
            loading={loadingKnowledgeStudioDocs}
            placeholder={t('workflowDesigner.knowledgeSourceStudioPlaceholder')}
            value={draft.knowledgeStudioDocumentIds || []}
            onChange={(ids) => updateDraft({ knowledgeStudioDocumentIds: ids || [] })}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(String(input || '').toLowerCase())
            }
            style={{ width: '100%' }}
          >
            {knowledgeStudioDocs.map((doc) => (
              <Select.Option key={doc.id} value={doc.id} label={doc.title}>
                {doc.title}
                {!doc.publishedVersionId ? ` — ${t('workflowDesigner.knowledgeSourceStudioDraft')}` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {draft.masterKb && (
          <>
            <Divider orientation="left" style={{ margin: '8px 0 12px' }}>
              {t('workflowDesigner.knowledgeStudioRagEnabled')}
            </Divider>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('workflowDesigner.knowledgeStudioRagHint')}
            />
            <Space align="center" style={{ marginBottom: 8 }}>
              <Switch
                checked={!!draft.knowledgeStudioRagEnabled}
                disabled={disabled || !ragEligible}
                onChange={(checked) => updateDraft({ knowledgeStudioRagEnabled: checked })}
              />
              <Typography.Text strong>{t('workflowDesigner.knowledgeStudioRagEnabled')}</Typography.Text>
            </Space>

            {/* RAG 引擎選擇器：自建管線 vs xAI Collections（xAI 預設供應商時隱藏自建管線選項） */}
            <Form.Item label={t('workflowDesigner.knowledgeStudioRagProvider')} style={{ marginBottom: 8 }}>
              <Radio.Group
                disabled={disabled}
                value={ragProvider}
                onChange={(e) => updateDraft({ knowledgeStudioRagProvider: e.target.value })}
              >
                {!isXaiDefaultProvider && (
                  <Radio value="local">{t('workflowDesigner.knowledgeStudioRagProviderLocal')}</Radio>
                )}
                <Radio value="xai-collections">{t('workflowDesigner.knowledgeStudioRagProviderXai')}</Radio>
              </Radio.Group>
              {isXaiDefaultProvider && (
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  {t('workflowDesigner.knowledgeStudioRagXaiAutoSelected')}
                </Typography.Text>
              )}
            </Form.Item>

            {ragProvider === 'xai-collections' && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={t('workflowDesigner.knowledgeStudioRagXaiHint')}
              />
            )}

            {draft.knowledgeStudioRagEnabled && (
              <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size="small">
                <Form.Item label={t('workflowDesigner.knowledgeStudioTopK')} style={{ marginBottom: 8 }}>
                  <InputNumber
                    min={1}
                    max={32}
                    disabled={disabled}
                    value={draft.knowledgeStudioTopK}
                    onChange={(v) => updateDraft({ knowledgeStudioTopK: v ?? 8 })}
                    style={{ width: 120 }}
                  />
                </Form.Item>
                <Form.Item label={t('workflowDesigner.knowledgeStudioMaxContextChars')} style={{ marginBottom: 8 }}>
                  <InputNumber
                    min={500}
                    max={50000}
                    step={500}
                    disabled={disabled}
                    value={draft.knowledgeStudioMaxContextChars}
                    onChange={(v) => updateDraft({ knowledgeStudioMaxContextChars: v ?? 8000 })}
                    style={{ width: 160 }}
                  />
                </Form.Item>
                {ragProvider === 'local' && (
                  <Form.Item label={t('workflowDesigner.knowledgeStudioEmbeddingProvider')} style={{ marginBottom: 0 }}>
                    <Select
                      allowClear
                      disabled={disabled}
                      loading={loadingEmbeddingProviders}
                      placeholder={t('workflowDesigner.knowledgeStudioEmbeddingProviderPlaceholder')}
                      value={draft.knowledgeStudioEmbeddingProviderKey || undefined}
                      onChange={(v) => updateDraft({ knowledgeStudioEmbeddingProviderKey: v || '' })}
                      style={{ width: '100%' }}
                    >
                      {embeddingProviders.map((p) => (
                        <Select.Option key={p.providerKey} value={p.providerKey}>
                          {p.displayName} ({p.providerKey}){p.active ? '' : ` — ${t('apiProviders.status.inactive')}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Space>
            )}
            <Form.Item
              label={t('workflowDesigner.chatBotRagAdditionalQuery')}
              tooltip={t('workflowDesigner.chatBotRagAdditionalQueryHelp')}
              style={{ marginTop: 8, marginBottom: 0 }}
            >
              <Input.TextArea
                rows={2}
                disabled={disabled}
                value={draft.ragAdditionalQuery || ''}
                onChange={(e) => updateDraft({ ragAdditionalQuery: e.target.value })}
                placeholder={t('workflowDesigner.chatBotRagAdditionalQueryPlaceholder')}
              />
            </Form.Item>
          </>
        )}

        <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
          {t('workflowDesigner.knowledgeBaseSettingsSectionDataset')}
        </Typography.Title>
        <Form.Item label={dsLabel}>
          <Select
            placeholder={t('workflowDesigner.dataSet.selectDataSetPlaceholder')}
            disabled={disabled}
            loading={loadingDataSets}
            value={draft.knowledgeBaseDataSetId || undefined}
            onChange={(value) => {
              updateDraft({
                knowledgeBaseDataSetId: value || '',
              });
            }}
            allowClear
          >
            {dataSets.map((ds) => (
              <Select.Option key={ds.id} value={ds.id}>
                {ds.name} ({ds.dataSourceType})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Divider orientation="left" style={{ margin: '12px 0 16px' }}>
          {t('workflowDesigner.knowledgeBaseSettingsQueryDivider')}
        </Divider>

        <Form.Item>
          <DataSetQueryConditionGroupsEditor
            conditionGroups={draft.knowledgeBaseQueryConditionGroups || []}
            onConditionGroupsChange={(groups) => updateDraft({ knowledgeBaseQueryConditionGroups: groups })}
            generateWhereClause={generateWhereClause}
            t={t}
            processVariables={processVariables}
            dataSetColumns={modalColumns}
            loadDataSetColumns={loadModalKbColumns}
            dataSetId={disabled ? '' : draft.knowledgeBaseDataSetId}
          />
        </Form.Item>

        {draft.masterKb && !!draft.knowledgeBaseDataSetId && (
          <>
            <Divider orientation="left" style={{ margin: '12px 0 16px' }}>
              {t('workflowDesigner.dataSet.orderByFields')}
            </Divider>
            <Form.Item>
              <DataSetQueryOrderByEditor
                orderByFields={draft.knowledgeBaseQueryOrderBy || []}
                onOrderByFieldsChange={(fields) => updateDraft({ knowledgeBaseQueryOrderBy: fields })}
                dataSetColumns={modalColumns}
                t={t}
              />
            </Form.Item>
          </>
        )}

        {/* ── 日曆事件 ─────────────────────────────────────────────────────── */}
        <Divider style={{ margin: '16px 0' }} />
        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
          {t('workflowDesigner.knowledgeBaseSettingsSectionCalendar')}
        </Typography.Title>

        <Space align="center" style={{ marginBottom: 4 }}>
          <Switch
            checked={!!draft.knowledgeBaseCalendarEnabled}
            onChange={(checked) => {
              updateDraft({
                knowledgeBaseCalendarEnabled: checked,
                ...(!checked
                  ? {
                      knowledgeBaseCalendarGroupIds: [],
                      knowledgeBaseCalendarHashtags: [],
                    }
                  : {}),
              });
            }}
          />
          <Typography.Text strong>{t('workflowDesigner.knowledgeBaseCalendarEnabledSwitch')}</Typography.Text>
        </Space>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          {t('workflowDesigner.knowledgeBaseCalendarEnabledHelp')}
        </Typography.Paragraph>

        {draft.knowledgeBaseCalendarEnabled && (
          <>
            <Form.Item label={t('workflowDesigner.knowledgeBaseCalendarFilterModeLabel')}>
              <Radio.Group
                value={draft.knowledgeBaseCalendarFilterMode || 'self'}
                onChange={(e) =>
                  updateDraft({
                    knowledgeBaseCalendarFilterMode: e.target.value,
                    knowledgeBaseCalendarGroupIds: [],
                    knowledgeBaseCalendarHashtags: [],
                  })
                }
              >
                <Radio value="self" style={{ display: 'block', marginBottom: 6 }}>
                  {t('workflowDesigner.knowledgeBaseCalendarFilterModeSelf')}
                </Radio>
                <Radio value="groupTag" style={{ display: 'block' }}>
                  {t('workflowDesigner.knowledgeBaseCalendarFilterModeGroupTag')}
                </Radio>
              </Radio.Group>
            </Form.Item>

            {calendarGroupTagMode && (
              <>
                <Form.Item label={t('workflowDesigner.knowledgeBaseCalendarGroupIds')}>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    loading={loadingBroadcastGroups}
                    placeholder={t('workflowDesigner.knowledgeBaseCalendarGroupIdsPlaceholder')}
                    value={draft.knowledgeBaseCalendarGroupIds || []}
                    onChange={(ids) => updateDraft({ knowledgeBaseCalendarGroupIds: ids || [] })}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(String(input || '').toLowerCase())
                    }
                    style={{ width: '100%' }}
                  >
                    {(broadcastGroups || []).map((g) => (
                      <Select.Option key={g.id} value={g.id} label={g.name}>
                        {g.color ? (
                          <span>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: g.color,
                                marginRight: 6,
                              }}
                            />
                            {g.name}
                          </span>
                        ) : (
                          g.name
                        )}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label={t('workflowDesigner.knowledgeBaseCalendarHashtags')}>
                  <Select
                    mode="tags"
                    allowClear
                    placeholder={t('workflowDesigner.knowledgeBaseCalendarHashtagsPlaceholder')}
                    value={draft.knowledgeBaseCalendarHashtags || []}
                    onChange={(tags) => updateDraft({ knowledgeBaseCalendarHashtags: tags || [] })}
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                    open={false}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item label={t('workflowDesigner.knowledgeBaseCalendarRelativeDays')}>
              <InputNumber
                min={0}
                max={365}
                value={draft.knowledgeBaseCalendarRelativeDays}
                onChange={(val) => updateDraft({ knowledgeBaseCalendarRelativeDays: val })}
                placeholder={t('workflowDesigner.knowledgeBaseCalendarRelativeDaysPlaceholder')}
                style={{ width: 160 }}
              />
            </Form.Item>
          </>
        )}
      </Modal>
    </>
  );
}
